import { SaltfishPlaylists } from "@bokio/components/Saltfish/Saltfish";
import { SettingsLangFactory } from "@bokio/lang";
import { getPlanDescription } from "@bokio/mobile-web-shared/areas/pricePlan/getPlanDescription";
import { getPlanName } from "@bokio/mobile-web-shared/areas/pricePlan/getPlanName";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { CURRENCY } from "@bokio/mobile-web-shared/SharedRedLibrary/data/src/currency";
import { formatMessage, formatNumberCurrency } from "@bokio/shared/utils/format";

import { trackfunnel_completedPurchasePlan } from "./funnel";
import { trackEvent } from "./t";

import type { FontelloIcons } from "@bokio/assets/fontello";

import BokioPlan = m.Entities.BokioPlan;
import SubscriptionInterval = m.Bokio.Common.Billing.Model.SubscriptionInterval;

type CashbackContract = m.Bokio.Common.Billing.Model.CashbackContract;

export const planValueOrder: BokioPlan[] = [
	BokioPlan.Free,
	BokioPlan.Trial_2025,
	BokioPlan.Basic_2025,
	BokioPlan.Premium,
	BokioPlan.Premium_2025,
	BokioPlan.Business_2025,
	BokioPlan.Pro,
	BokioPlan.Plus_2025,
];
export const planIntervalOrder: SubscriptionInterval[] = [
	SubscriptionInterval.Month,
	SubscriptionInterval.Quarter,
	SubscriptionInterval.Year,
];

export { getPlanDescription, getPlanName };

export const getIntervalName = (interval: SubscriptionInterval | undefined) => {
	const settingsLang = SettingsLangFactory();
	switch (interval) {
		case SubscriptionInterval.Year:
			return settingsLang.Plans_BuyModal_Yearly;
		case SubscriptionInterval.Quarter:
			return settingsLang.Plans_BuyModal_Quarterly;
		case SubscriptionInterval.Month:
			return settingsLang.Plans_BuyModal_Monthly;
		default:
			return "";
	}
};

export type IntervalTextType = "billing" | "billing-cycle";
export const getIntervalText = (interval: SubscriptionInterval, type: IntervalTextType = "billing") => {
	const settingsLang = SettingsLangFactory();

	switch (true) {
		case interval === SubscriptionInterval.Month && type === "billing":
			return settingsLang.Plans_BuyModal_MonthlyBilling;
		case interval === SubscriptionInterval.Month && type === "billing-cycle":
			return settingsLang.Plans_Monthly;

		case interval === SubscriptionInterval.Quarter && type === "billing":
			return settingsLang.Plans_BuyModal_QuarterlyBilling;
		case interval === SubscriptionInterval.Quarter && type === "billing-cycle":
			return settingsLang.Plans_Quaterly;

		case interval === SubscriptionInterval.Year && type === "billing":
			return settingsLang.Plans_BuyModal_YearlyBilling;
		case interval === SubscriptionInterval.Year && type === "billing-cycle":
			return settingsLang.Plans_Yearly;

		default:
			return "";
	}
};

export const getPerIntervalText = (interval: SubscriptionInterval) => {
	const settingsLang = SettingsLangFactory();
	switch (interval) {
		case SubscriptionInterval.Month:
			return settingsLang.Plans_BuyModal_PerMonth;
		case SubscriptionInterval.Quarter:
			return settingsLang.Plans_BuyModal_PerQuarter;
		case SubscriptionInterval.Year:
			return settingsLang.Plans_BuyModal_PerYear;
		default:
			return "";
	}
};

export const getIntervalPaymentText = (interval: SubscriptionInterval) => {
	const settingsLang = SettingsLangFactory();
	switch (interval) {
		case SubscriptionInterval.Month:
			return settingsLang.Plans_EPB_Text_Monthly;
		case SubscriptionInterval.Quarter:
			return settingsLang.Plans_EPB_Text_Quarterly;
		case SubscriptionInterval.Year:
			return settingsLang.Plans_EPB_Text_Yearly;
		default:
			return "";
	}
};

export const getIntervalSubscriptionCostText = (interval: SubscriptionInterval) => {
	const settingsLang = SettingsLangFactory();
	switch (interval) {
		case SubscriptionInterval.Month:
			return settingsLang.Plans_Checkout_SubscriptionCostMonthly;
		case SubscriptionInterval.Quarter:
			return settingsLang.Plans_Checkout_SubscriptionCostQuarterly;
		case SubscriptionInterval.Year:
			return settingsLang.Plans_Checkout_SubscriptionCostYearly;
		default:
			return "";
	}
};

export const getIntervalOptions = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel,
	interval: SubscriptionInterval,
): m.Bokio.Common.Billing.Model.BillingIntervalOption => {
	switch (interval) {
		case SubscriptionInterval.Month:
			return plan.IntervalOptions.Monthly;
		case SubscriptionInterval.Quarter:
			return plan.IntervalOptions.Quarterly;
		case SubscriptionInterval.Year:
			return plan.IntervalOptions.Yearly;
		default:
			throw new Error(`Unknown interval ${interval}.`);
	}
};

export const isIntervalAvailableInterval = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel,
	interval: SubscriptionInterval | undefined,
) => {
	switch (interval) {
		case SubscriptionInterval.Month:
			return (
				plan.IntervalOptions.Monthly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available
			);
		case SubscriptionInterval.Quarter:
			return (
				plan.IntervalOptions.Quarterly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available
			);
		case SubscriptionInterval.Year:
			return (
				plan.IntervalOptions.Yearly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available
			);
		default:
			return false;
	}
};

export const getSelectedTierFromPlans = (
	currentPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	nextPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel | undefined,
): m.Entities.BokioPlan => {
	if (nextPlan && nextPlan?.Key === BokioPlan.Free && currentPlan.Key === BokioPlan.Pro) {
		return m.Entities.BokioPlan.Pro;
	}

	if ((nextPlan && nextPlan?.Key === BokioPlan.Free) || (!nextPlan && currentPlan.Key === BokioPlan.Free)) {
		return m.Entities.BokioPlan.Premium;
	}

	return nextPlan && nextPlan?.Key !== BokioPlan.Free ? nextPlan.Key : currentPlan.Key;
};

export const getSelectedIntervalFromPlans = (
	currentPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	nextPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel | undefined,
) => {
	return nextPlan && nextPlan?.Key !== BokioPlan.Free
		? nextPlan.SubscriptionInterval
		: currentPlan.SubscriptionInterval;
};

export const getDefaultSelectedInterval = (plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel) => {
	if (isIntervalAvailableInterval(plan, SubscriptionInterval.Quarter)) {
		return SubscriptionInterval.Quarter;
	} else if (isIntervalAvailableInterval(plan, SubscriptionInterval.Year)) {
		return SubscriptionInterval.Year;
	} else if (isIntervalAvailableInterval(plan, SubscriptionInterval.Month)) {
		return SubscriptionInterval.Month;
	}

	return undefined;
};

export const getBasePrice = (plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel) => {
	if (plan.IntervalOptions.Monthly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available) {
		return plan.IntervalOptions.Monthly.PriceExclVat;
	} else if (
		plan.IntervalOptions.Quarterly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available
	) {
		return plan.IntervalOptions.Quarterly.PriceExclVat;
	} else if (
		plan.IntervalOptions.Yearly.Visibility === m.Bokio.Common.Billing.Model.BillingIntervalVisibility.Available
	) {
		return plan.IntervalOptions.Yearly.PriceExclVat;
	}
	return 0;
};

export const getNrOfDecimals = (currency: CURRENCY) => {
	return currency === CURRENCY.GBP ? 2 : 0;
};

export const calculateCashback = (
	contract: m.Bokio.Common.Billing.Model.CashbackContract,
	capital: number,
	cardspend: number,
) => {
	const maxCardSpend = 100000000; //We don't have any max limit on card spend, but just to avoid overflow lets count with 100M

	const cardSpendCashback =
		(Math.min(Math.max(cardspend, 0), maxCardSpend) * contract.CardSpendCashbackPercentage) / 100;

	const cappedDepositedCapital =
		contract.DepositedCapitalCashbackCap !== undefined
			? Math.min(Math.max(capital, 0), contract.DepositedCapitalCashbackCap)
			: Math.max(capital, 0);

	const depositedCapitalCashback = ((cappedDepositedCapital / 12) * contract.DepositedCapitalCashbackPercentage) / 100;
	const totalCashback = cardSpendCashback + depositedCapitalCashback;

	return { totalCashback, cardSpendCashback, depositedCapitalCashback };
};

export const formatPriceplanPercentage = (percentage: number) => {
	return (percentage ? percentage : 0) + "%";
};

export const getMonthsFromInterval = (interval: SubscriptionInterval) => {
	switch (interval) {
		case SubscriptionInterval.Month:
			return 1;
		case SubscriptionInterval.Quarter:
			return 3;
		case SubscriptionInterval.Year:
			return 12;
		default:
			throw new Error(`Unknown interval ${interval}.`);
	}
};

export const getBilledMessage = (subscriptionInterval: SubscriptionInterval) => {
	const settingsLang = SettingsLangFactory();
	switch (subscriptionInterval) {
		case SubscriptionInterval.Month:
			return settingsLang.Plans_BilledMonthly;
		case SubscriptionInterval.Quarter:
			return settingsLang.Plans_BilledQuarterly;
		case SubscriptionInterval.Year:
			return settingsLang.Plans_BilledYearly;
		default:
			return "";
	}
};

export const getCurrentPlanPaymentText = (
	interval: SubscriptionInterval,
	isDowngrading: boolean,
	paymentAmount: number | undefined,
	dateOfInterest: m.Day | undefined,
	currency: CURRENCY,
) => {
	const settingsLang = SettingsLangFactory();

	let formatedPaymentAmount;
	if (paymentAmount !== undefined) {
		formatedPaymentAmount = formatNumberCurrency(paymentAmount, currency, getNrOfDecimals(currency));
	} else {
		throw new Error(`Didn't receive a paymentAmount.`);
	}

	if (!isDowngrading) {
		switch (interval) {
			case SubscriptionInterval.Month:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_MonthlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_NextPayment, dateOfInterest?.toISOString())}`;
			case SubscriptionInterval.Quarter:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_QuarterlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_NextPayment, dateOfInterest?.toISOString())}`;
			case SubscriptionInterval.Year:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_YearlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_NextPayment, dateOfInterest?.toISOString())}`;
			default:
				throw new Error(`Unknown interval ${interval}.`);
		}
	} else {
		switch (interval) {
			case SubscriptionInterval.Month:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_MonthlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_PlanIsEnding, dateOfInterest?.toISOString())}`;
			case SubscriptionInterval.Quarter:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_QuarterlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_PlanIsEnding, dateOfInterest?.toISOString())}`;
			case SubscriptionInterval.Year:
				return `${formatMessage(
					settingsLang.Plans_CurrentPlanBox_YearlyPayments,
					formatedPaymentAmount,
				)} ${formatMessage(settingsLang.Plans_CurrentPlanBox_PlanIsEnding, dateOfInterest?.toISOString())}`;
			default:
				throw new Error(`Unknown interval ${interval}.`);
		}
	}
};

export const hasFailedPaymentState = (paymentState: m.Entities.BillingSubscriptionPaymentState | undefined) => {
	return (
		paymentState === m.Entities.BillingSubscriptionPaymentState.BlockedDueToMissingPayment ||
		paymentState === m.Entities.BillingSubscriptionPaymentState.PastDue
	);
};

export type PlanChange = "Upgrade" | "Downgrade" | "IntervalChange" | "Cancel" | "OldPlanGoingToNew";
export type PlanChangeInfo = {
	plan: m.Entities.BokioPlan;
	interval: m.Bokio.Common.Billing.Model.SubscriptionInterval;
	inTrial: boolean;
};
export type PlanChangeType = {
	type: PlanChange;
	upgrade: boolean;
	downgrade: boolean;
};
const planChangeType = (newPlan: PlanChangeInfo, oldPlan: PlanChangeInfo, planDowngrade: boolean): PlanChange => {
	switch (true) {
		case (oldPlan.plan === m.Entities.BokioPlan.Premium || oldPlan.plan === m.Entities.BokioPlan.Pro) &&
			newPlan.plan.includes("2025"):
			return "Downgrade";
		case newPlan.plan === m.Entities.BokioPlan.Free:
			return "Cancel";
		case !oldPlan.inTrial && newPlan.plan === oldPlan.plan && oldPlan.interval !== newPlan.interval:
			return "IntervalChange";
		case planDowngrade:
			return "Downgrade";

		default:
			return "Upgrade";
	}
};
export const getPlanChangeType = (oldPlan: PlanChangeInfo, newPlan: PlanChangeInfo): PlanChangeType => {
	const planUpgrade = oldPlan.inTrial || planValueOrder.indexOf(oldPlan.plan) < planValueOrder.indexOf(newPlan.plan);
	const intervalUpgrade =
		newPlan.plan === oldPlan.plan &&
		planIntervalOrder.indexOf(oldPlan.interval) < planIntervalOrder.indexOf(newPlan.interval);

	const planDowngrade = !planUpgrade;
	const intervalDowngrade =
		newPlan.plan === oldPlan.plan &&
		planIntervalOrder.indexOf(newPlan.interval) < planIntervalOrder.indexOf(oldPlan.interval);

	const pcType: PlanChange = planChangeType(newPlan, oldPlan, planDowngrade);

	if (
		(oldPlan.plan === m.Entities.BokioPlan.Premium || oldPlan.plan === m.Entities.BokioPlan.Pro) &&
		newPlan.plan.includes("2025")
	) {
		return {
			type: "OldPlanGoingToNew",
			upgrade: false,
			downgrade: false,
		};
	}

	return {
		type: pcType,
		upgrade: pcType === "Cancel" ? false : pcType === "IntervalChange" ? intervalUpgrade : planUpgrade,
		downgrade: pcType === "Cancel" ? false : pcType === "IntervalChange" ? intervalDowngrade : planDowngrade,
	};
};

export const pricePlanChangeIsUpgrade = (oldPlan: BokioPlan, newPlan: BokioPlan, oldPlanIsTrial = false): boolean => {
	if (oldPlan === BokioPlan.Premium && newPlan === BokioPlan.Premium && oldPlanIsTrial) {
		return true;
	}

	return planValueOrder.indexOf(newPlan) > planValueOrder.indexOf(oldPlan);
};

export const pricePlanChangeIsDowngrade = (oldPlan: BokioPlan, newPlan: BokioPlan, oldPlanIsTrial = false): boolean => {
	if (oldPlanIsTrial || oldPlan === newPlan) {
		return false;
	}

	return planValueOrder.indexOf(newPlan) < planValueOrder.indexOf(oldPlan);
};

const featureAndPrice = (featureName: string, price: number, duration: string) => {
	return `${featureName} (${formatNumberCurrency(price, CURRENCY.SEK, 0)}/${duration})`;
};

export const getNrOfUsersIncluded = (plan: BokioPlan): number | undefined => {
	switch (plan) {
		case BokioPlan.Basic_2025:
		case BokioPlan.Premium_2025:
			return 1;
		case BokioPlan.Business_2025:
			return 2;
		case BokioPlan.Plus_2025:
			return 3;
		case BokioPlan.Trial_2025:
		default:
			return undefined;
	}
};

export const getCostPerExtraUser = (plan: BokioPlan): number => {
	switch (plan) {
		case BokioPlan.Basic_2025:
		case BokioPlan.Premium_2025:
		case BokioPlan.Business_2025:
		case BokioPlan.Plus_2025:
			return 49;
		case BokioPlan.Trial_2025:
		default:
			return 0;
	}
};

export type PlanItemListDetail = {
	Text: string;
	Icon?: FontelloIcons | "swish";
};

export type PlanItemType = "Perk" | "AddOn" | "NotIncluded";

export type PlanItemFeatureModalType =
	| "BBA"
	| "Invoicing"
	| "Cashback"
	| "SalaryAndExpenses"
	| "BokioAPI"
	| "AccountingwithTags"
	| "SmartTemplates"
	| "DebbieAndLiveChat"
	| "DebbieChat";

export type PlanItem = {
	Type: PlanItemType;
	Name: string;
	Details?: PlanItemListDetail[];
	Route?: string;
	bbaIcon?: boolean;
	betaIcon?: boolean;
	planItemFeatureType?: PlanItemFeatureModalType;
};

export type PlanItemList = {
	Heading?: string;
	PlanItems: PlanItem[];
};

export const getPlanItems = (
	plan: BokioPlan,
	companyId: string,
	bokioBusinessAccountAvailable: boolean,
	cashback: CashbackContract,
): PlanItemList | undefined => {
	const settingsLang = SettingsLangFactory();

	const RecordWithTemplates: PlanItem = {
		Type: "Perk",
		Name: settingsLang.Plans_RPU_Items_RecordWithTemplates,
		planItemFeatureType: "SmartTemplates",
	};

	const DebbieAndLiveChat: PlanItem = {
		Type: "Perk",
		Name: settingsLang.DebbieAndLiveChat,
		planItemFeatureType: "DebbieAndLiveChat",
	};

	const DebbieChat: PlanItem = {
		Type: "Perk",
		Name: settingsLang.DebbieChat,
		planItemFeatureType: "DebbieChat",
	};

	const InvoiceAddon: PlanItem = {
		Type: "AddOn",
		Name: featureAndPrice(settingsLang.PricePlanFeature_Invoicing, 89, settingsLang.PricePerMonth),
	};

	const InvoicePerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_Invoicing,
		planItemFeatureType: "Invoicing",
	};

	const BankConnectionAddon: PlanItem = {
		Type: "AddOn",
		Name: featureAndPrice(settingsLang.PricePlanFeature_AutomaticBankImport, 49, settingsLang.PricePerMonth),
	};

	const BankConnectionPerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_AutomaticBankImport,
	};

	const EmployeesAndExpenses: PlanItem = {
		Type: "Perk",
		Name: settingsLang.Plans_RPU_Items_EmployeesAndExpenses,
		planItemFeatureType: "SalaryAndExpenses",
	};

	const IntegrationsAddon: PlanItem = {
		Type: "AddOn",
		Name: settingsLang.PricePlanFeature_PublicApi,
		planItemFeatureType: "BokioAPI",
		Details: [{ Text: featureAndPrice("", 89, settingsLang.PricePerMonth) }],
	};

	const IntegrationsPerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_PublicApi,
		planItemFeatureType: "BokioAPI",
		Details: [{ Text: "" }], // GF 2025-06-13: This is here to make it line up with the other plans that have a cost associated with Integrations.
	};

	const LiveChatPerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_LiveChat,
	};

	const PhoneSupportPerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_TelephoneSupport,
	};

	const ProjectAccountingTagPerk: PlanItem = {
		Type: "Perk",
		Name: settingsLang.PricePlanFeature_Tags,
		Details: [{ Text: "" }], // GF 2025-06-13: This is here to make it line up with the other plans that have a cost associated with ProjectAccountingWithTags.
		planItemFeatureType: "AccountingwithTags",
	};

	const ProjectAccountingTagAddOn: PlanItem = {
		Type: "AddOn",
		Name: settingsLang.PricePlanFeature_Tags,
		Details: [{ Text: featureAndPrice("", 49, settingsLang.PricePerMonth) }],
		planItemFeatureType: "AccountingwithTags",
	};

	if (plan === BokioPlan.Premium) {
		return {
			Heading: settingsLang.Plans_RPU_Perks_Premium_Heading,
			PlanItems: [
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_Premium_SmarterBookkeeping },
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_Premium_MoreProfessionalInvoicing },
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_Premium_SalaryServices },
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_Premium_BankServices },
			],
		};
	} else if (plan === BokioPlan.Pro) {
		return {
			Heading: settingsLang.Plans_RPU_Perks_PremiumPlus_Heading,
			PlanItems: [
				{
					Type: "Perk",
					Name: formatMessage(
						settingsLang.Plans_RPU_Perks_PremiumPlus_CashbackCardSpend,
						formatPriceplanPercentage(cashback.CardSpendCashbackPercentage),
					),
				},
				{
					Type: "Perk",
					Name: formatMessage(
						settingsLang.Plans_RPU_Perks_PremiumPlus_CashbackCapital,
						formatPriceplanPercentage(cashback.DepositedCapitalCashbackPercentage),
					),
				},
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_PremiumPlus_FreeAccountingAssistance },
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_PremiumPlus_PhoneSupport },
				{ Type: "Perk", Name: settingsLang.Plans_RPU_Perks_PremiumPlus_PrioSupport },
			],
		};
	} else if (plan === BokioPlan.Basic_2025) {
		return {
			PlanItems: [
				RecordWithTemplates,
				DebbieChat,
				InvoiceAddon,
				BankConnectionAddon,
				{
					...EmployeesAndExpenses,
					Type: "NotIncluded",
					Details: [{ Text: "" }],
				},
				{ ...IntegrationsPerk, Type: "NotIncluded" },
				{ ...ProjectAccountingTagPerk, Type: "NotIncluded" },
				{ ...LiveChatPerk, Type: "NotIncluded" },
				{ ...PhoneSupportPerk, Type: "NotIncluded" },
			],
		};
	} else if (plan === BokioPlan.Premium_2025) {
		return {
			PlanItems: [
				RecordWithTemplates,
				DebbieChat,
				InvoicePerk,
				BankConnectionPerk,
				{
					...EmployeesAndExpenses,
					Details: [{ Text: settingsLang.Plans_RPU_Items_1Employee }],
				},
				IntegrationsAddon,
				ProjectAccountingTagAddOn,
				{ ...LiveChatPerk, Type: "NotIncluded" },
				{ ...PhoneSupportPerk, Type: "NotIncluded" },
			],
		};
	} else if (plan === BokioPlan.Business_2025) {
		return {
			PlanItems: [
				RecordWithTemplates,
				DebbieAndLiveChat,
				InvoicePerk,
				BankConnectionPerk,
				{
					...EmployeesAndExpenses,
					Details: [
						{
							Text: settingsLang.Plans_RPU_Items_MultipleEmployees,
						},
					],
				},
				IntegrationsPerk,
				ProjectAccountingTagPerk,
				LiveChatPerk,
				PhoneSupportPerk,
			],
		};
	} else if (plan === BokioPlan.Plus_2025) {
		return {
			PlanItems: [
				RecordWithTemplates,
				DebbieAndLiveChat,
				InvoicePerk,
				BankConnectionPerk,
				{
					...EmployeesAndExpenses,
					Details: [
						{
							Text: settingsLang.Plans_RPU_Items_MultipleEmployees,
						},
					],
				},
				IntegrationsPerk,
				ProjectAccountingTagPerk,
				LiveChatPerk,
				PhoneSupportPerk,
			],
		};
	}

	// {...BokioBusinessAccount, Type: "AddOn"}]

	return undefined;
};

export const getPlanPerksWithBBA = (
	plan: BokioPlan,
	companyId: string,
	cashback: CashbackContract,
): PlanItemList | undefined => {
	return getPlanItems(plan, companyId, true, cashback);
};

export const getBBAFeatureList = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel,
	contract: CashbackContract,
): BBAPlanPerks => {
	if (plan.Key === m.Entities.BokioPlan.Plus_2025 || plan.Key === m.Entities.BokioPlan.Pro) {
		return {
			HasBankAccountsCardsAndSwish: true,
			HasCashBackOnBalance: true,
			HasCashBackOnCardPurchases: true,
			cashBackBalanceValue: contract.DepositedCapitalCashbackPercentage,
			cashBackTransactionValue: contract.CardSpendCashbackPercentage,
		};
	} else {
		return {
			HasBankAccountsCardsAndSwish: true,
			HasCashBackOnBalance: false,
			HasCashBackOnCardPurchases: false,
			cashBackBalanceValue: contract.DepositedCapitalCashbackPercentage,
			cashBackTransactionValue: contract.CardSpendCashbackPercentage,
		};
	}
};

export const trackPlanEvent = async (
	oldPlan: BokioPlan,
	newPlan: BokioPlan,
	newInterval?: SubscriptionInterval,
	currency?: string,
	planPriceExVat?: number,
) => {
	const oldIndex = planValueOrder.indexOf(oldPlan);
	const newIndex = planValueOrder.indexOf(newPlan);

	if (oldIndex < newIndex) {
		trackEvent("BokioPlan", "Upgrade", `SelectedPlan${newPlan}`);
		if (newInterval && currency) {
			trackfunnel_completedPurchasePlan(newPlan, newInterval, currency, planPriceExVat);
		}
	}
	if (oldIndex === newIndex) {
		trackEvent("BokioPlan", "Same", `SelectedPlan${newPlan}`);
	}
	if (oldIndex > newIndex) {
		trackEvent("BokioPlan", "Downgrade", `SelectedPlan${newPlan}`);
	}
};

export const monthsInInterval = (interval: SubscriptionInterval) => {
	switch (interval) {
		case m.Bokio.Common.Billing.Model.SubscriptionInterval.Month:
			return 1;
		case m.Bokio.Common.Billing.Model.SubscriptionInterval.Quarter:
			return 3;
		case m.Bokio.Common.Billing.Model.SubscriptionInterval.Year:
			return 12;
		default:
			throw new Error("The interval doesn't exist.");
	}
};

export const is2025PaidPlan = (plan: BokioPlan | undefined) => {
	switch (plan) {
		case BokioPlan.Basic_2025:
		case BokioPlan.Premium_2025:
		case BokioPlan.Business_2025:
		case BokioPlan.Plus_2025:
			return true;
		case BokioPlan.Trial_2025:
		default:
			return false;
	}
};

// GF 2024-03-28: Returns the monetary value compared to the base offer. Always positive (negative discount would mean negative value).
export const calculateDiscount = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel | undefined,
	interval: m.Bokio.Common.Billing.Model.SubscriptionInterval,
) => {
	if (!plan) {
		return 0;
	}

	const intervalOptions = getIntervalOptions(plan, interval);
	const discountPrice = intervalOptions.Discount?.DiscountedPriceExclVat;
	const currentMonthlyPrice = discountPrice ?? intervalOptions.PriceExclVat;

	const baseMonthlyPrice = getBasePrice(plan);
	const discountValue = (baseMonthlyPrice - currentMonthlyPrice) * monthsInInterval(interval);

	if (discountValue < 0) {
		throw new Error("Discount value is negative.");
	}

	return discountValue;
};

export const calculateDiscountPercentage = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel | undefined,
	interval: m.Bokio.Common.Billing.Model.SubscriptionInterval,
) => {
	if (!plan) {
		return null;
	}
	const intervalOptions = getIntervalOptions(plan, interval);
	const discountPrice = intervalOptions.Discount?.DiscountedPriceExclVat;
	const currentPrice = discountPrice ?? intervalOptions.PriceExclVat;
	const basePrice = getBasePrice(plan);
	const percentage = (1 - currentPrice / basePrice) * 100;
	return percentage.toFixed(0) + "%";
};

export type PlanTransitionState = "starts" | "renews" | "ends" | "none";

interface PlanTransitionArgs {
	renderedPlan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel;
	currentPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel;
	nextPlan?: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel;
	renderedInterval?: m.Bokio.Common.Billing.Model.SubscriptionInterval;
}

export interface BBAPlanPerks {
	HasBankAccountsCardsAndSwish: boolean;
	HasCashBackOnBalance: boolean;
	HasCashBackOnCardPurchases: boolean;
	cashBackBalanceValue: number;
	cashBackTransactionValue: number;
}

export const isEndsState = ({ currentPlan, nextPlan, renderedPlan, renderedInterval }: PlanTransitionArgs) => {
	const renderedPlanIsCurrentPlanWhenCancelled = !!(
		nextPlan &&
		nextPlan.Key == BokioPlan.Free &&
		currentPlan.Key == renderedPlan.Key &&
		currentPlan.SubscriptionInterval == renderedInterval
	);

	const renderedPlanIsCurrentPlanWhenDowngrading = !!(
		nextPlan &&
		(pricePlanChangeIsDowngrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing) ||
			pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing)) &&
		currentPlan.Key == renderedPlan.Key &&
		currentPlan.SubscriptionInterval == renderedInterval
	);

	const renderPlanIsChangingInterval = !!(
		!currentPlan.IsTrialing &&
		nextPlan &&
		currentPlan.Key == renderedPlan.Key &&
		nextPlan.Key == currentPlan.Key &&
		planIntervalOrder.indexOf(nextPlan.SubscriptionInterval) <
			planIntervalOrder.indexOf(currentPlan.SubscriptionInterval) &&
		renderedInterval != nextPlan.SubscriptionInterval
	);

	const scheduledChangeFromRenderedOldPlanTo2025Plan = !!(
		!currentPlan.IsTrialing &&
		!is2025PaidPlan(currentPlan.Key) &&
		nextPlan &&
		is2025PaidPlan(nextPlan.Key) &&
		renderedPlan.Key == currentPlan.Key &&
		currentPlan.SubscriptionInterval == renderedInterval
	);

	const illegalScheduledOldPlanUpgrade = !!(
		!currentPlan.IsTrialing &&
		nextPlan &&
		!is2025PaidPlan(currentPlan.Key) &&
		!is2025PaidPlan(nextPlan.Key) &&
		renderedPlan.Key == currentPlan.Key &&
		nextPlan.SubscriptionInterval != renderedInterval
	);

	return (
		renderedPlanIsCurrentPlanWhenCancelled ||
		renderedPlanIsCurrentPlanWhenDowngrading ||
		scheduledChangeFromRenderedOldPlanTo2025Plan ||
		renderPlanIsChangingInterval ||
		illegalScheduledOldPlanUpgrade
	);
};

export const isNoneState = ({ currentPlan, nextPlan, renderedPlan, renderedInterval }: PlanTransitionArgs) => {
	const blockedFreePlan = currentPlan.Key == m.Entities.BokioPlan.Free;
	const trialingWithNoCommitment = currentPlan.IsTrialing && !nextPlan;

	const onTrialWithScheduledChangeToOtherPlan = !!(
		currentPlan.IsTrialing &&
		nextPlan &&
		nextPlan.Key != renderedPlan.Key
	);

	const newPlanStartingOnDifferentInterval = !!(
		renderedPlan.Key != currentPlan.Key &&
		nextPlan &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval != renderedInterval
	);

	const renderedPlanIsNotNextPlan = !!(
		(!currentPlan.IsTrialing && nextPlan && nextPlan.Key != renderedPlan.Key && renderedPlan.Key != currentPlan.Key) ||
		(!currentPlan.IsTrialing && !nextPlan && renderedPlan.Key != currentPlan.Key)
	);

	const oldPlansImpossibleUpgradeCase = !!(
		!currentPlan.IsTrialing &&
		currentPlan.Key != renderedPlan.Key &&
		nextPlan &&
		pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, false) &&
		!is2025PaidPlan(currentPlan.Key) &&
		!is2025PaidPlan(nextPlan.Key)
	);

	const illegalScheduledIntervalChangeToHigherTierInterval =
		nextPlan &&
		is2025PaidPlan(nextPlan.Key) &&
		is2025PaidPlan(currentPlan.Key) &&
		nextPlan.Key == currentPlan.Key &&
		currentPlan.SubscriptionInterval != nextPlan.SubscriptionInterval &&
		renderedPlan.Key == currentPlan.Key &&
		!pricePlanChangeIsDowngrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing) &&
		!pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing) &&
		planIntervalOrder.indexOf(nextPlan.SubscriptionInterval) >
			planIntervalOrder.indexOf(currentPlan.SubscriptionInterval);

	const illegalScheduledIntervalChangeToHigherTierIntervalOnOldPlans =
		nextPlan &&
		!is2025PaidPlan(nextPlan.Key) &&
		!is2025PaidPlan(currentPlan.Key) &&
		nextPlan.Key == currentPlan.Key &&
		currentPlan.SubscriptionInterval != nextPlan.SubscriptionInterval &&
		renderedPlan.Key == currentPlan.Key &&
		!pricePlanChangeIsDowngrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing) &&
		!pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, currentPlan.IsTrialing) &&
		planIntervalOrder.indexOf(nextPlan.SubscriptionInterval) >
			planIntervalOrder.indexOf(currentPlan.SubscriptionInterval);

	const illegalScheduledUpgradeFrom2025To2025 =
		nextPlan &&
		pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, false) &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval == renderedInterval &&
		is2025PaidPlan(currentPlan.Key) &&
		is2025PaidPlan(nextPlan.Key);

	const illegalScheduledChangeToOldFrom2025 =
		nextPlan &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval == renderedInterval &&
		is2025PaidPlan(currentPlan.Key) &&
		!is2025PaidPlan(nextPlan.Key);

	const renewingOnRenderedPlanButDifferentInterval =
		!nextPlan && renderedPlan.Key == currentPlan.Key && currentPlan.SubscriptionInterval != renderedInterval;

	const inTrialLookingAtScheduledPlanButDifferentInterval = !!(
		currentPlan.IsTrialing &&
		nextPlan &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval != renderedInterval
	);

	const renderedPlanHasNoRelationToCurrentPlanOrNextPlan =
		(!currentPlan.IsTrialing && renderedPlan.Key != currentPlan.Key && nextPlan && nextPlan.Key != renderedPlan.Key) ||
		(nextPlan &&
			renderedInterval != nextPlan?.SubscriptionInterval &&
			nextPlan.Key != renderedPlan.Key &&
			renderedPlan.Key != currentPlan.Key);

	const renderedIntervalHasNoRelationToCurrentIntervalAndNextPlanUnrelatedToPlan =
		currentPlan.SubscriptionInterval != renderedInterval && nextPlan && nextPlan.Key != renderedPlan.Key;

	const illegalRenewalWhileOnDifferentInterval =
		nextPlan &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval != renderedInterval &&
		renderedInterval != currentPlan.SubscriptionInterval;

	const illegallyGoingToOldPlanFromNewPlan = !!(
		nextPlan &&
		is2025PaidPlan(currentPlan.Key) &&
		!is2025PaidPlan(nextPlan.Key) &&
		nextPlan.Key != BokioPlan.Free &&
		currentPlan.Key == renderedPlan.Key &&
		currentPlan.SubscriptionInterval == renderedInterval
	);

	const illegallyGoingToNewPlanFromNewPlan = !!(
		!currentPlan.IsTrialing &&
		nextPlan &&
		is2025PaidPlan(currentPlan.Key) &&
		is2025PaidPlan(nextPlan.Key) &&
		pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key, false) &&
		currentPlan.Key == renderedPlan.Key &&
		currentPlan.SubscriptionInterval == renderedInterval
	);

	const illegalSamePlanEverything = !!(
		!currentPlan.IsTrialing &&
		nextPlan &&
		currentPlan.Key == nextPlan.Key &&
		nextPlan.Key == renderedPlan.Key &&
		renderedInterval == currentPlan.SubscriptionInterval &&
		nextPlan.SubscriptionInterval == currentPlan.SubscriptionInterval
	);

	return (
		trialingWithNoCommitment ||
		onTrialWithScheduledChangeToOtherPlan ||
		renderedPlanIsNotNextPlan ||
		newPlanStartingOnDifferentInterval ||
		oldPlansImpossibleUpgradeCase ||
		renewingOnRenderedPlanButDifferentInterval ||
		inTrialLookingAtScheduledPlanButDifferentInterval ||
		blockedFreePlan ||
		illegalScheduledIntervalChangeToHigherTierInterval ||
		renderedPlanHasNoRelationToCurrentPlanOrNextPlan ||
		renderedIntervalHasNoRelationToCurrentIntervalAndNextPlanUnrelatedToPlan ||
		illegalRenewalWhileOnDifferentInterval ||
		illegalScheduledUpgradeFrom2025To2025 ||
		illegalScheduledChangeToOldFrom2025 ||
		illegalScheduledIntervalChangeToHigherTierIntervalOnOldPlans ||
		illegallyGoingToOldPlanFromNewPlan ||
		illegallyGoingToNewPlanFromNewPlan ||
		illegalSamePlanEverything
	);
};

export const isStartsState = ({ currentPlan, nextPlan, renderedPlan, renderedInterval }: PlanTransitionArgs) => {
	const onTrialGoingToRenderedPlanOnSelectedInterval = !!(
		currentPlan.IsTrialing &&
		nextPlan &&
		nextPlan.SubscriptionInterval == renderedInterval &&
		nextPlan.Key == renderedPlan.Key
	);

	const scheduledDowngradeToRenderedPlan = !!(
		nextPlan &&
		currentPlan.Key != nextPlan.Key &&
		nextPlan.Key == renderedPlan.Key &&
		nextPlan.SubscriptionInterval == renderedInterval &&
		pricePlanChangeIsDowngrade(currentPlan.Key, nextPlan.Key)
	);

	const scheduledDowngradeOnSamePlan = !!(
		nextPlan &&
		nextPlan.Key == currentPlan.Key &&
		nextPlan.SubscriptionInterval == renderedInterval &&
		nextPlan.Key == renderedPlan.Key &&
		planIntervalOrder.indexOf(nextPlan.SubscriptionInterval) <
			planIntervalOrder.indexOf(currentPlan.SubscriptionInterval)
	);

	const scheduledChangeTo2025PlanFromOldPlan =
		!currentPlan.IsTrialing &&
		nextPlan &&
		pricePlanChangeIsUpgrade(currentPlan.Key, nextPlan.Key) &&
		is2025PaidPlan(nextPlan.Key) &&
		!is2025PaidPlan(currentPlan.Key) &&
		currentPlan.Key != BokioPlan.Free &&
		is2025PaidPlan(renderedPlan.Key) &&
		renderedInterval == nextPlan.SubscriptionInterval &&
		nextPlan.Key == renderedPlan.Key;

	return (
		onTrialGoingToRenderedPlanOnSelectedInterval ||
		scheduledDowngradeToRenderedPlan ||
		scheduledChangeTo2025PlanFromOldPlan ||
		scheduledDowngradeOnSamePlan
	);
};

export const isRenewsState = ({ currentPlan, nextPlan, renderedPlan, renderedInterval }: PlanTransitionArgs) => {
	const isRenewingOnCurrentPlanAndRenderedInterval =
		!nextPlan && currentPlan.Key == renderedPlan.Key && currentPlan.SubscriptionInterval == renderedInterval;

	return isRenewingOnCurrentPlanAndRenderedInterval;
};

export const getPlanTransitionStatus = (
	renderedPlan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel,
	currentPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	nextPlan?: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	renderedInterval?: m.Bokio.Common.Billing.Model.SubscriptionInterval,
): PlanTransitionState => {
	const args = { renderedPlan, currentPlan, nextPlan, renderedInterval };

	// GF 2025-07-07: "none" takes precedence and can occur together with other states.
	// The others are mutually exclusive, which is covered in the tests.
	if (isNoneState(args)) {
		return "none";
	} else if (isEndsState(args)) {
		return "ends";
	} else if (isStartsState(args)) {
		return "starts";
	} else if (isRenewsState(args)) {
		return "renews";
	} else {
		return "none";
	}
};

export type SubscriptionButtonState =
	| "GetStarted"
	| "Upgrade"
	| "Downgrade"
	| "Change"
	| "Cancel"
	| "UndoCancel"
	| "OldPlanGoingToNew"
	| null;
export const getSubscriptionButtonState = (
	plan: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel,
	currentPlan: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	nextPlan?: m.Bokio.Backbone.Web.Controllers.Settings.ActivatedPricePlanModel,
	selectedInterval?: m.Bokio.Common.Billing.Model.SubscriptionInterval,
): SubscriptionButtonState | null => {
	//#region -- null --

	if (plan.Key === m.Entities.BokioPlan.Pro && selectedInterval === SubscriptionInterval.Year) {
		// No yearly interval for PremiumPlus (Pro)
		return null;
	}
	if (plan.Key === m.Entities.BokioPlan.Plus_2025 && selectedInterval === SubscriptionInterval.Year) {
		// No yearly interval for Plus_2025
		return null;
	}
	if (
		(currentPlan.IsTrialing || currentPlan.Key === m.Entities.BokioPlan.Free) &&
		(plan.Key === m.Entities.BokioPlan.Premium || plan.Key === m.Entities.BokioPlan.Pro) &&
		(nextPlan?.Key === m.Entities.BokioPlan.Basic_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025)
	) {
		// Choosing old plans is not allowed if you're not already on an old plan, so this is not legal
		return null;
	}
	if (
		(currentPlan.Key === m.Entities.BokioPlan.Basic_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Premium_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Business_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Plus_2025) &&
		(plan.Key === m.Entities.BokioPlan.Premium || plan.Key === m.Entities.BokioPlan.Pro)
	) {
		// Going back to old plans is not allowed, so this is not legal
		return null;
	}
	if (
		(currentPlan.Key === m.Entities.BokioPlan.Basic_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Premium_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Business_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Plus_2025) &&
		(nextPlan?.Key === m.Entities.BokioPlan.Premium || nextPlan?.Key === m.Entities.BokioPlan.Pro)
	) {
		// Going back to old plans is not allowed, so this is not legal
		return null;
	}
	if (
		(currentPlan.Key === m.Entities.BokioPlan.Basic_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Premium_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Business_2025) &&
		(nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025) &&
		planValueOrder.indexOf(nextPlan?.Key) > planValueOrder.indexOf(currentPlan.Key)
	) {
		// Upgrades are immediate, so this case is not legal
		return null;
	}
	if (
		(currentPlan.Key === m.Entities.BokioPlan.Basic_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Premium_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Business_2025) &&
		nextPlan?.Key === currentPlan.Key &&
		currentPlan.SubscriptionInterval === SubscriptionInterval.Quarter &&
		nextPlan?.SubscriptionInterval === SubscriptionInterval.Year
	) {
		// Upgrades are immediate, so this case is not legal
		return null;
	}
	//#endregion

	//#region -- "UndoCancel" --
	if (
		plan.Key === currentPlan.Key &&
		selectedInterval === currentPlan.SubscriptionInterval &&
		nextPlan &&
		(nextPlan.Key !== currentPlan.Key || nextPlan.SubscriptionInterval !== currentPlan.SubscriptionInterval) &&
		(nextPlan?.Key === m.Entities.BokioPlan.Basic_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium ||
			nextPlan?.Key === m.Entities.BokioPlan.Pro ||
			nextPlan?.Key === m.Entities.BokioPlan.Free)
	) {
		// Viewing the current plan and interval, with a planned switch to any other plan
		return "UndoCancel";
	}
	//#endregion

	//#region -- "Cancel" --
	if (
		!currentPlan.IsTrialing &&
		nextPlan &&
		nextPlan.Key === plan.Key &&
		nextPlan.SubscriptionInterval === selectedInterval
	) {
		return "Cancel";
	}

	if (
		!currentPlan.IsTrialing &&
		!nextPlan &&
		currentPlan.Key === plan.Key &&
		currentPlan.SubscriptionInterval === selectedInterval
	) {
		// Viewing the current plan and interval, with no scheduled change
		return "Cancel";
	}

	if (
		currentPlan.IsTrialing &&
		nextPlan &&
		nextPlan.Key === plan.Key &&
		nextPlan.SubscriptionInterval === selectedInterval
	) {
		// Viewing the scheduled plan and interval in trial
		return "Cancel";
	}
	//#endregion

	//#region -- "GetStarted" --
	if (
		(currentPlan.IsTrialing && nextPlan?.Key === m.Entities.BokioPlan.Free) ||
		(currentPlan.IsTrialing && nextPlan === undefined) ||
		(currentPlan.Key === m.Entities.BokioPlan.Free && nextPlan?.Key === m.Entities.BokioPlan.Free) ||
		(currentPlan.Key === m.Entities.BokioPlan.Free && !nextPlan)
	) {
		return "GetStarted";
	}
	//#endregion

	//#region -- "OldPlanGoingToNew" --
	if (
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025) &&
		(nextPlan?.Key === m.Entities.BokioPlan.Premium || nextPlan?.Key === m.Entities.BokioPlan.Pro)
	) {
		return "OldPlanGoingToNew";
	}

	if (
		!currentPlan.IsTrialing &&
		(currentPlan.Key === m.Entities.BokioPlan.Premium || currentPlan.Key === m.Entities.BokioPlan.Pro) &&
		!nextPlan &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025)
	) {
		return "OldPlanGoingToNew";
	}

	if (
		!currentPlan.IsTrialing &&
		(currentPlan.Key === m.Entities.BokioPlan.Premium || currentPlan.Key === m.Entities.BokioPlan.Pro) &&
		nextPlan &&
		(nextPlan.Key === m.Entities.BokioPlan.Premium ||
			nextPlan.Key === m.Entities.BokioPlan.Pro ||
			nextPlan.Key === m.Entities.BokioPlan.Free) &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025)
	) {
		return "OldPlanGoingToNew";
	}
	//#endregion

	//#region -- "Change" --
	if (nextPlan && nextPlan.Key === plan.Key && selectedInterval && selectedInterval !== nextPlan.SubscriptionInterval) {
		return "Change";
	}

	if (
		!currentPlan.IsTrialing &&
		currentPlan.Key === plan.Key &&
		selectedInterval &&
		selectedInterval !== currentPlan.SubscriptionInterval
	) {
		return "Change";
	}
	//#endregion

	//#region "Upgrade"
	if (
		(currentPlan.IsTrialing || currentPlan.Key === m.Entities.BokioPlan.Free) &&
		nextPlan &&
		nextPlan.Key !== m.Entities.BokioPlan.Free &&
		planValueOrder.indexOf(plan.Key) > planValueOrder.indexOf(nextPlan.Key)
	) {
		return "Upgrade";
	}
	if (
		!currentPlan.IsTrialing &&
		(currentPlan.Key === m.Entities.BokioPlan.Premium || currentPlan.Key === m.Entities.BokioPlan.Pro) &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025) &&
		nextPlan &&
		(nextPlan?.Key === m.Entities.BokioPlan.Basic_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025) &&
		planValueOrder.indexOf(plan.Key) > planValueOrder.indexOf(nextPlan.Key)
	) {
		return "Upgrade";
	}

	if (
		!currentPlan.IsTrialing &&
		(currentPlan.Key === m.Entities.BokioPlan.Basic_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Premium_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Business_2025 ||
			currentPlan.Key === m.Entities.BokioPlan.Plus_2025) &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025) &&
		nextPlan &&
		(nextPlan?.Key === m.Entities.BokioPlan.Basic_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025) &&
		planValueOrder.indexOf(plan.Key) > planValueOrder.indexOf(nextPlan.Key)
	) {
		return "Upgrade";
	}
	// GF 2025-02-18: This should really be written more readable.
	if (
		(plan.Key === currentPlan.Key &&
			currentPlan.Key !== m.Entities.BokioPlan.Free &&
			!currentPlan.IsTrialing &&
			pricePlanChangeIsUpgrade(currentPlan.Key, plan.Key, currentPlan.IsTrialing)) ||
		((!currentPlan.IsTrialing ||
			(plan.Key === m.Entities.BokioPlan.Pro &&
				currentPlan.IsTrialing &&
				nextPlan?.Key === m.Entities.BokioPlan.Premium)) &&
			plan.Key === m.Entities.BokioPlan.Pro &&
			currentPlan.Key !== m.Entities.BokioPlan.Pro &&
			currentPlan.Key !== m.Entities.BokioPlan.Free)
	) {
		return "Upgrade";
	}
	if (
		currentPlan.Key === m.Entities.BokioPlan.Basic_2025 &&
		plan.Key !== m.Entities.BokioPlan.Basic_2025 &&
		pricePlanChangeIsUpgrade(currentPlan.Key, plan.Key, currentPlan.IsTrialing)
	) {
		return "Upgrade";
	}

	if (
		currentPlan.Key === m.Entities.BokioPlan.Premium_2025 &&
		plan.Key !== m.Entities.BokioPlan.Premium_2025 &&
		pricePlanChangeIsUpgrade(currentPlan.Key, plan.Key, currentPlan.IsTrialing)
	) {
		return "Upgrade";
	}

	if (currentPlan.Key === m.Entities.BokioPlan.Business_2025 && plan.Key === m.Entities.BokioPlan.Plus_2025) {
		return "Upgrade";
	}

	//#endregion

	//#region "Downgrade"
	if (
		(currentPlan.IsTrialing || currentPlan.Key === m.Entities.BokioPlan.Free) &&
		nextPlan &&
		planValueOrder.indexOf(plan.Key) < planValueOrder.indexOf(nextPlan.Key)
	) {
		return "Downgrade";
	}

	if (
		!currentPlan.IsTrialing &&
		(currentPlan.Key === m.Entities.BokioPlan.Premium || currentPlan.Key === m.Entities.BokioPlan.Pro) &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025 ||
			plan.Key === m.Entities.BokioPlan.Plus_2025) &&
		nextPlan &&
		(nextPlan?.Key === m.Entities.BokioPlan.Basic_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Premium_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Business_2025 ||
			nextPlan?.Key === m.Entities.BokioPlan.Plus_2025) &&
		planValueOrder.indexOf(nextPlan.Key) > planValueOrder.indexOf(plan.Key)
	) {
		return "Downgrade";
	}
	if (
		plan.Key === m.Entities.BokioPlan.Premium &&
		currentPlan.Key === m.Entities.BokioPlan.Pro &&
		!currentPlan.IsTrialing
	) {
		return "Downgrade";
	}

	if (
		plan.Key === m.Entities.BokioPlan.Basic_2025 &&
		currentPlan.Key !== m.Entities.BokioPlan.Basic_2025 &&
		currentPlan.Key !== m.Entities.BokioPlan.Free &&
		!currentPlan.IsTrialing
	) {
		return "Downgrade";
	}
	if (
		currentPlan.Key === m.Entities.BokioPlan.Business_2025 &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 || plan.Key === m.Entities.BokioPlan.Premium_2025)
	) {
		return "Downgrade";
	}

	if (
		currentPlan.Key === m.Entities.BokioPlan.Plus_2025 &&
		(plan.Key === m.Entities.BokioPlan.Basic_2025 ||
			plan.Key === m.Entities.BokioPlan.Premium_2025 ||
			plan.Key === m.Entities.BokioPlan.Business_2025)
	) {
		return "Downgrade";
	}

	//#endregion

	// Uncovered cases
	return null;
};

export const priceForSubscriptionInterval = (
	plan: m.Entities.BokioPlan,
	interval: m.Bokio.Common.Billing.Model.SubscriptionInterval,
	plans: m.Bokio.Backbone.Web.Controllers.Settings.PriceplansPlanModel[],
): number => {
	const intervalOpt = () => {
		switch (true) {
			case interval === m.Bokio.Common.Billing.Model.SubscriptionInterval.Year:
				return "Yearly";
			case interval === m.Bokio.Common.Billing.Model.SubscriptionInterval.Quarter:
				return "Quarterly";
			default:
				return "Monthly";
		}
	};
	const timesMonths =
		interval === m.Bokio.Common.Billing.Model.SubscriptionInterval.Year
			? 12
			: interval === m.Bokio.Common.Billing.Model.SubscriptionInterval.Quarter
				? 3
				: 1;

	const price = [...plans]
		.filter(p => {
			return plan === p.Key;
		})
		.map(p => {
			return p.IntervalOptions[intervalOpt()].PriceExclVat;
		})
		.shift();

	return (price && price * timesMonths) || -1;
};

export const getSaltfishPlaylistFromPlan = (bokioPlan: BokioPlan) => {
	switch (bokioPlan) {
		case BokioPlan.Basic_2025:
			return SaltfishPlaylists.BasicPricing;
		case BokioPlan.Premium_2025:
			return SaltfishPlaylists.PremiumPricing;
		case BokioPlan.Business_2025:
			return SaltfishPlaylists.BusinessPricing;
		case BokioPlan.Plus_2025:
			return SaltfishPlaylists.PlusPricing;
		default:
			return;
	}
};
